import type { Candidate, PreventivoRow } from "../services/preventiviApi";

const PIPELINE_POSITIVE = new Set(["codice", "exact", "acceptable"]);
const TYPE_RANK: Record<string, number> = { codice: 0, exact: 1, acceptable: 2 };

const sortKey = (c: Candidate): [number, number] => [
  TYPE_RANK[c.match_type ?? ""] ?? 9,
  -(c.match_percent ?? 0),
];

const compareCandidates = (a: Candidate, b: Candidate): number => {
  const [ra, pa] = sortKey(a);
  const [rb, pb] = sortKey(b);
  return ra !== rb ? ra - rb : pa - pb;
};

const isAutoSelectable = (c: Candidate): boolean =>
  PIPELINE_POSITIVE.has(c.match_type ?? "");

/**
 * Pick the best positively-classified candidate for auto-selection.
 * Prefers same preventivo categoria; falls back to other categories only
 * when none qualify.
 */
export const pickBestPositiveCandidate = (
  candidates: Candidate[],
  preventivoCategoria?: string | null,
): Candidate | null => {
  const positive = candidates.filter(isAutoSelectable);
  if (!positive.length) return null;

  if (preventivoCategoria) {
    const sameCat = positive.filter(
      (c) => (c.metadata?.categoria ?? "") === preventivoCategoria,
    );
    if (sameCat.length) {
      return [...sameCat].sort(compareCandidates)[0];
    }
  }

  return [...positive].sort(compareCandidates)[0];
};

/**
 * Best positive candidate for display when no explicit selection exists.
 * Includes manual/legacy selections in the positive pool.
 */
export const pickBestDisplayCandidate = (
  candidates: Candidate[],
  preventivoCategoria?: string | null,
): Candidate | null => {
  const isPositive = (c: Candidate) =>
    isAutoSelectable(c) || c.manually_selected === true || c.match_type === "manual";

  const positive = candidates.filter(isPositive);
  if (!positive.length) return null;

  if (preventivoCategoria) {
    const sameCat = positive.filter(
      (c) => (c.metadata?.categoria ?? "") === preventivoCategoria,
    );
    if (sameCat.length) {
      return [...sameCat].sort(compareCandidates)[0];
    }
  }

  return [...positive].sort(compareCandidates)[0];
};

/**
 * isPositiveCandidate — a candidate counts as a real match only if it was
 * positively classified by the pipeline (codice/exact/acceptable) or
 * explicitly picked by the user (manually_selected, or the legacy
 * match_type === "manual").
 */
export const isPositiveCandidate = (c: Candidate): boolean =>
  c.manually_selected === true || c.match_type === "manual" || isAutoSelectable(c);

/**
 * getEffectiveMatch — SINGLE source of truth for "what is the actual match of
 * this row". A row has an effective match only when ``selected_item_uid`` is
 * set AND that candidate is positively classified. There is deliberately NO
 * fallback to `candidates[0]` or to "any positive candidate in the list":
 * an unselected or all-excluded row has no effective match, full stop — this
 * is what fixes the "Nessun match valido" / phantom price+match-line bug
 * where the UI showed data from a candidate the pipeline had rejected.
 */
export const getEffectiveMatch = (row: PreventivoRow): Candidate | null => {
  if (!row.selected_item_uid) return null;
  const sel = (row.candidates ?? []).find((c) => c.uid === row.selected_item_uid);
  return sel && isPositiveCandidate(sel) ? sel : null;
};

/** hasValidMatch — true iff the row has an effective (selected + positive) match. */
export const hasValidMatch = (row: PreventivoRow): boolean => getEffectiveMatch(row) !== null;

// Sigle diverse che indicano tutte la stessa unità "a conteggio" (un pezzo/
// elemento), solo scritte con convenzioni diverse a seconda del documento:
// mirror di `_COUNT_UNIT_ALIASES` in backend/src/extractor/udm.py — le tiene
// sincronizzate qui perché il confronto UdM per il triangolino di alert
// avviene lato frontend, senza round-trip al backend.
const COUNT_UNIT_ALIASES = new Set([
  "cad", "cadauno", "cadauna", "caduno",
  "pz", "pezzo", "pezzi",
  "n", "nr", "numero",
  "corpo",
  "a",
]);
const COUNT_UNIT_CANONICAL = "cad";

/**
 * Normalize a unit of measure for comparison: lowercase, strip punctuation/spaces,
 * then collapse interchangeable "a conteggio" units (pz/n/nr/corpo/cad/...) to a
 * single canonical form. Matches backend `normalize_udm`.
 */
export const normalizeUdm = (value?: string | null): string => {
  const s = (value ?? "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  return COUNT_UNIT_ALIASES.has(s) ? COUNT_UNIT_CANONICAL : s;
};

/**
 * isUdmMismatch — normalized comparison between the row's own unit of measure
 * and the matched candidate's udm. Returns false whenever either side is missing,
 * since we only want to flag an actual discrepancy, not a missing value.
 */
export const isUdmMismatch = (rowUnit?: string | null, candidateUdm?: string | null): boolean => {
  const a = normalizeUdm(rowUnit);
  const b = normalizeUdm(candidateUdm);
  if (!a || !b) return false;
  return a !== b;
};

/**
 * getRowMetadata — metadata to display/aggregate for a row: manual overrides
 * always win, falling back to the effective match's metadata only (never to
 * a rejected/unselected candidate's metadata).
 */
export const getRowMetadata = (row: PreventivoRow): Record<string, any> => {
  const matchMeta = getEffectiveMatch(row)?.metadata ?? {};
  return { ...matchMeta, ...(row.overridden_metadata ?? {}) };
};
